/**
 * ROI — a trust-sensitive page. Two visually distinct zones that must never
 * blend: the customer's own measured data (headline) and illustrative
 * reference economics (clearly marked, not a forecast).
 */
import { useState } from 'react';
import { api, qs } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { RoiResult } from '../api/types';
import { Card, ErrorBox, Loading, Metric, Unavailable } from '../components/Metric';

const usd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export function RoiPage() {
  const [days, setDays] = useState(30);
  const from = new Date(Date.now() - days * 86400_000).toISOString();
  const to = new Date().toISOString();

  const roi = useApi<RoiResult>(() => api.get<RoiResult>(`/api/v1/metrics/roi${qs({ from, to })}`), [days]);

  if (roi.loading) return <Loading label="Computing…" />;
  if (roi.error) return <ErrorBox message={roi.error} />;
  if (!roi.data) return null;

  const { measured, illustrativeReference: ref } = roi.data;

  return (
    <>
      <div className="btn-row" style={{ marginBottom: 16 }}>
        <div className="seg" role="group" aria-label="Window">
          {[7, 30, 90].map((d) => (
            <button key={d} className={days === d ? 'on' : ''} onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {/* ZONE 1 — the customer's own data. The headline. */}
      <div className="zone" style={{ borderLeft: '3px solid var(--brand)' }}>
        <div className="card-head" style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 15 }}>Zone 1 · Your measured results</h2>
          <span className="tag brand">your data</span>
        </div>
        <div className="grid grid-3">
          <Metric label="Cycles inspected" value={measured.cyclesInWindow} from={`Last ${days} days, this organisation`} />
          <Metric label="Defect cycles" value={measured.defectCycles} from="Model flags + human labels" />
          <Metric
            label="Measured defect rate"
            tone={measured.defectRatePct == null ? 'default' : 'warn'}
            value={measured.defectRatePct != null ? measured.defectRatePct.toFixed(2) : undefined}
            unit={measured.defectRatePct != null ? '%' : undefined}
            from="Defect cycles ÷ cycles inspected"
          >
            <Unavailable reason="No cycles recorded in this window." />
          </Metric>
        </div>
        <div className="card-note">{measured.note}</div>
      </div>

      {/* ZONE 2 — reference economics. Always labelled, visually distinct. */}
      <div className="zone illustrative" style={{ marginTop: 16 }}>
        <div className="card-head" style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 15 }}>Zone 2 · Reference economics</h2>
          <span className="tag warn">Illustrative — not your result</span>
        </div>
        <div className="callout warn" style={{ marginBottom: 14 }}>
          <strong>These are industry reference figures, not a forecast</strong>
          {ref.label}. They describe a representative machine so you can
          sanity-check the scale of the problem. Your actual saving depends on
          your part value, your scrap rate and your defect mix — use Zone 1, not
          this zone, for a business case. Source: {ref.source}
        </div>
        <div className="grid grid-3">
          <Metric
            label="Scrap loss per machine / month"
            value={usd(ref.scrapLossPerMachinePerMonthUsd)}
            sub={`${ref.baselineScrapRatePct}% baseline scrap × ${ref.partsPerMonth.toLocaleString()} parts × ${usd(ref.partValueUsd)} part value`}
          />
          <Metric
            label="Mould crash exposure"
            value={usd(ref.moldCrashTotalUsd)}
            sub="Per event, including tooling damage and downtime"
          />
          <Metric
            label="Indicative annual licence"
            value={`${usd(ref.indicativeAnnualLicenseUsd[0])}–${usd(ref.indicativeAnnualLicenseUsd[1])}`}
            sub={`Per ${ref.pricingUnit}, per year`}
          />
        </div>
        <div className="card-note">
          Reference machine cost {usd(ref.machineCostUsd)}. Hardware is passed
          through at cost as CapEx and is not bundled into the licence.
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Card title="What this deliberately does not do">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--ink-2)' }}>
            <li style={{ marginBottom: 6 }}>It does not combine the two zones into a single saving figure.</li>
            <li style={{ marginBottom: 6 }}>
              It does not claim a mould-crash avoidance benefit, because this
              platform cannot stop a machine and therefore cannot prevent a
              crash by arresting motion.
            </li>
            <li>It does not annualise a short window without telling you it is extrapolating.</li>
          </ul>
        </Card>
      </div>
    </>
  );
}
